import { useState, useEffect } from 'react';
import { formatCOP } from '../../utils/currencyFormat';
import {
  getPlans,
  createPlan,
  updatePlan,
  deletePlan,
  type Plan,
  type CreatePlanData
} from '../../api/plans.api';

export default function Planes() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<CreatePlanData>({
    nombre: '',
    descripcion: '',
    precio: 0,
    periodo: 'MENSUAL',
    maxEspacios: 100,
    maxUsuarios: 10,
    caracteristicas: [],
    activo: true
  });

  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setIsLoading(true);
    try {
      const data = await getPlans();
      setPlans(data);
      setError(null);
    } catch (err) {
      console.error('Error cargando planes:', err);
      setError('Error al cargar los planes. Intente nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      precio: 0,
      periodo: 'MENSUAL',
      maxEspacios: 100,
      maxUsuarios: 10,
      caracteristicas: [],
      activo: true
    });
    setIsEditing(false);
    setSelectedPlanId(null);
    setShowModal(true);
  };

  const handleOpenEdit = (plan: Plan) => {
    setFormData({
      nombre: plan.nombre,
      descripcion: plan.descripcion,
      precio: plan.precio,
      periodo: plan.periodo,
      maxEspacios: plan.maxEspacios,
      maxUsuarios: plan.maxUsuarios,
      caracteristicas: plan.caracteristicas,
      activo: plan.activo
    });
    setIsEditing(true);
    setSelectedPlanId(plan.id);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditing && selectedPlanId) {
        await updatePlan(selectedPlanId, formData);
        setSuccessMessage('Plan actualizado exitosamente');
      } else {
        await createPlan(formData);
        setSuccessMessage('Plan creado exitosamente');
      }
      setShowModal(false);
      loadPlans();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error guardando plan:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error al guardar el plan');
      } else {
        setError('Error al guardar el plan');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (plan: Plan) => {
    if (!window.confirm(`¿Estás seguro de eliminar el plan "${plan.nombre}"?`)) return;

    try {
      await deletePlan(plan.id);
      setSuccessMessage('Plan eliminado exitosamente');
      loadPlans();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: unknown) {
      console.error('Error eliminando plan:', err);
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Error al eliminar el plan');
      } else {
        setError('Error al eliminar el plan');
      }
    }
  };

  const handleToggleStatus = async (plan: Plan) => {
    try {
      await updatePlan(plan.id, { activo: !plan.activo });
      setSuccessMessage(`Plan ${!plan.activo ? 'activado' : 'desactivado'} exitosamente`);
      loadPlans();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error cambiando estado:', err);
      setError('Error al cambiar el estado del plan');
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        caracteristicas: [...formData.caracteristicas, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      caracteristicas: formData.caracteristicas.filter((_, i) => i !== index)
    });
  };

  const filteredPlans = plans.filter(plan => 
    plan.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando planes...</p>
          </div>
        </div>
    );
  }

  return (
      <div className="space-y-6">
        {/* Mensajes de feedback */}
        {successMessage && (
          <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
            <i className="ri-check-line"></i>
            {successMessage}
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <i className="ri-error-warning-line text-red-500 text-xl"></i>
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError(null)} className="ml-auto">
              <i className="ri-close-line text-red-500"></i>
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Planes y Suscripciones</h1>
            <p className="text-sm text-slate-500 mt-1">Gestiona los planes disponibles para las empresas clientes</p>
          </div>
          <div className="flex gap-3">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
              <input
                type="text"
                placeholder="Buscar plan..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm w-full md:w-64"
              />
            </div>
            <button 
              onClick={handleOpenCreate}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition whitespace-nowrap"
            >
              <i className="ri-add-line text-lg"></i>
              <span className="font-medium">Nuevo Plan</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center">
                <i className="ri-vip-crown-line text-lg text-teal-600"></i>
              </div>
              <span className="text-sm text-slate-500">Planes Activos</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">{plans.filter(p => p.activo).length}</p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <i className="ri-building-line text-lg text-blue-600"></i>
              </div>
              <span className="text-sm text-slate-500">Empresas Totales</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {plans.reduce((acc, p) => acc + (p.empresasActivas || 0), 0)}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-lg text-green-600"></i>
              </div>
              <span className="text-sm text-slate-500">Ingresos Potenciales</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              {formatCOP(plans.reduce((acc, p) => acc + (p.precio * (p.empresasActivas || 0)), 0))}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                <i className="ri-line-chart-line text-lg text-purple-600"></i>
              </div>
              <span className="text-sm text-slate-500">Promedio/Empresa</span>
            </div>
            <p className="text-3xl font-bold text-slate-800">
              ${plans.length > 0 
                  ? (plans.reduce((acc, p) => acc + p.precio, 0) / plans.length).toLocaleString(undefined, { maximumFractionDigits: 0 }) 
                  : 0}
            </p>
          </div>
        </div>

        {/* Planes Grid */}
        {filteredPlans.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
            <i className="ri-vip-crown-line text-5xl text-slate-200 mb-4"></i>
            <h3 className="text-lg font-medium text-slate-800">No hay planes registrados</h3>
            <p className="text-slate-500">Crea el primer plan de suscripción para comenzar</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredPlans.map((plan) => (
              <div key={plan.id} className={`bg-white rounded-2xl border-2 transition p-8 relative ${plan.activo ? 'border-slate-200 hover:border-teal-500' : 'border-slate-100 opacity-75'}`}>
                {!plan.activo && (
                  <div className="absolute top-4 right-4 px-2 py-1 bg-slate-100 text-slate-600 text-xs font-semibold rounded-lg">
                    INACTIVO
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">{plan.nombre}</h3>
                  <p className="text-sm text-slate-500 mb-4 line-clamp-2 min-h-[40px]">{plan.descripcion}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-teal-600">{formatCOP(plan.precio)}</span>
                    <span className="text-slate-500 lowercase">/{plan.periodo}</span>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">Espacios</span>
                    <span className="font-semibold text-slate-800">{plan.maxEspacios.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">Usuarios</span>
                    <span className="font-semibold text-slate-800">
                      {plan.maxUsuarios.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <span className="text-sm text-slate-600">Empresas activas</span>
                    <span className="font-semibold text-teal-600">{plan.empresasActivas || 0}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-6 min-h-[100px]">
                  {plan.caracteristicas.slice(0, 4).map((caracteristica, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <i className="ri-check-line text-teal-600 mt-0.5"></i>
                      <span className="text-sm text-slate-600">{caracteristica}</span>
                    </div>
                  ))}
                  {plan.caracteristicas.length > 4 && (
                    <p className="text-xs text-slate-400 pl-6">+ {plan.caracteristicas.length - 4} más...</p>
                  )}
                </div>

                <div className="flex gap-2">
                  <button 
                    onClick={() => handleOpenEdit(plan)}
                    className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition font-medium whitespace-nowrap text-sm"
                  >
                    Editar
                  </button>
                  <button 
                    onClick={() => handleDelete(plan)}
                    className="px-4 py-2.5 border border-red-100 text-red-500 rounded-xl hover:bg-red-50 transition font-medium whitespace-nowrap text-sm"
                    title="Eliminar"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                  <button 
                    onClick={() => handleToggleStatus(plan)}
                    className={`px-4 py-2.5 rounded-xl transition font-medium whitespace-nowrap text-sm ${plan.activo ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' : 'bg-green-100 text-green-600 hover:bg-green-200'}`}
                    title={plan.activo ? "Desactivar" : "Activar"}
                  >
                    <i className={plan.activo ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modal Crear/Editar */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">
                  {isEditing ? 'Editar Plan' : 'Nuevo Plan'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  className="w-10 h-10 rounded-xl hover:bg-slate-100 flex items-center justify-center transition"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del Plan *</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                    <textarea
                      value={formData.descripcion}
                      onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 h-24 resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Precio (COP) *</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="number"
                        value={formData.precio === 0 ? '' : formData.precio}
                        onChange={(e) => setFormData({...formData, precio: e.target.value === '' ? 0 : Number(e.target.value)})}
                        className="w-full pl-8 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                        min="0"
                        placeholder="0"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Periodo *</label>
                    <select
                      value={formData.periodo}
                      onChange={(e) => setFormData({...formData, periodo: e.target.value as any})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="MENSUAL">Mensual</option>
                      <option value="TRIMESTRAL">Trimestral</option>
                      <option value="SEMESTRAL">Semestral</option>
                      <option value="ANUAL">Anual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Espacios Máximos</label>
                    <input
                      type="number"
                      value={formData.maxEspacios}
                      onChange={(e) => setFormData({...formData, maxEspacios: Number(e.target.value)})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                      min="1"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Usuarios Máximos</label>
                    <input
                      type="number"
                      value={formData.maxUsuarios}
                      onChange={(e) => setFormData({...formData, maxUsuarios: Number(e.target.value)})}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                      min="1"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Características</label>
                    <div className="flex gap-2 mb-3">
                      <input
                        type="text"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                        className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="Ej: Soporte 24/7"
                      />
                      <button 
                        type="button" 
                        onClick={addFeature}
                        className="px-4 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-700 transition"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formData.caracteristicas.map((feature, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <span className="text-sm text-slate-600">{feature}</span>
                          <button 
                            type="button" 
                            onClick={() => removeFeature(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <i className="ri-close-circle-line"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-200">
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition font-medium"
                    disabled={isSubmitting}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition font-medium disabled:bg-teal-300 flex items-center justify-center gap-2"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Guardando...
                      </>
                    ) : (
                      'Guardar Plan'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
  );
}
